import { useCallback, useEffect, useRef, useState } from 'react';
import { BrandRiskAnalysis } from '../../../shared/types';
import { scanApi } from '@/services/api';

const MAX_CONCURRENT = 5;
const TIMEOUT_MS = 15_000;

export type BrandRiskStatus = 'idle' | 'analyzing' | 'done' | 'error';

export function useBrandRiskQueue(
  onResult: (domain: string, analysis: BrandRiskAnalysis) => void
) {
  const activeRef = useRef(new Set<string>());
  const cacheRef = useRef(new Map<string, BrandRiskAnalysis>());
  const errorsRef = useRef(new Set<string>());
  const visibleQueueRef = useRef<string[]>([]);
  const deferredQueueRef = useRef<string[]>([]);
  const allQueuedRef = useRef(new Set<string>());
  const onResultRef = useRef(onResult);

  const [, forceUpdate] = useState(0);
  const bump = useCallback(() => forceUpdate(v => v + 1), []);

  useEffect(() => { onResultRef.current = onResult; });

  // processQueueRef avoids circular useCallback deps
  const processQueueRef = useRef<() => void>(() => {});
  processQueueRef.current = () => {
    while (activeRef.current.size < MAX_CONCURRENT && visibleQueueRef.current.length > 0) {
      const domain = visibleQueueRef.current.shift()!;
      if (cacheRef.current.has(domain) || activeRef.current.has(domain)) continue;

      activeRef.current.add(domain);
      errorsRef.current.delete(domain);
      bump();

      const timeoutId = window.setTimeout(() => {
        if (activeRef.current.has(domain)) {
          activeRef.current.delete(domain);
          errorsRef.current.add(domain);
          bump();
          processQueueRef.current();
        }
      }, TIMEOUT_MS);

      scanApi.analyzeBrandRisk(
        domain,
        undefined,
        (analysis) => {
          clearTimeout(timeoutId);
          cacheRef.current.set(domain, analysis);
          activeRef.current.delete(domain);
          onResultRef.current(domain, analysis);
          bump();
          processQueueRef.current();
        },
        () => {
          clearTimeout(timeoutId);
          activeRef.current.delete(domain);
          errorsRef.current.add(domain);
          bump();
          processQueueRef.current();
        }
      ).catch(() => {
        clearTimeout(timeoutId);
        activeRef.current.delete(domain);
        errorsRef.current.add(domain);
        bump();
        processQueueRef.current();
      });
    }
  };

  const queue = useCallback((domain: string) => {
    if (allQueuedRef.current.has(domain)) return;
    allQueuedRef.current.add(domain);
    deferredQueueRef.current.push(domain);
  }, []);

  const markVisible = useCallback((domain: string) => {
    const i = deferredQueueRef.current.indexOf(domain);
    if (i !== -1) deferredQueueRef.current.splice(i, 1);
    if (!cacheRef.current.has(domain) && !activeRef.current.has(domain) && !errorsRef.current.has(domain)) {
      visibleQueueRef.current.push(domain);
      processQueueRef.current();
    }
  }, []);

  const retry = useCallback((domain: string) => {
    errorsRef.current.delete(domain);
    allQueuedRef.current.delete(domain);
    visibleQueueRef.current.push(domain);
    processQueueRef.current();
    bump();
  }, [bump]);

  const getStatus = (domain: string): BrandRiskStatus => {
    if (cacheRef.current.has(domain)) return 'done';
    if (activeRef.current.has(domain)) return 'analyzing';
    if (errorsRef.current.has(domain)) return 'error';
    return 'idle';
  };

  const getResult = (domain: string) => cacheRef.current.get(domain);

  return { queue, markVisible, retry, getStatus, getResult };
}
